// src/components/CompanyPaymentPlan/CompanyPaymentPlanPage.js
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../Navbar/Navbar";
import api from "../Axios/axios";
import "./CompanyPaymentPlanPage.scss";

// ---------- Yardımcılar ----------

const toDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

// Eski/bozuk tarihleri de fallback ile kullanabilen normalizasyon
const normalizePaymentDate = (p, primary, fallbackDate = null) => {
  const tryList = [];

  if (primary === "transfer") {
    tryList.push(
      p.deliveryDate,
      p.DeliveryDate,
      p.invoiceDate,
      p.InvoiceDate,
      p.chequeOrBondDate,
      p.ChequeOrBondDate
    );
  } else if (primary === "cheque") {
    tryList.push(
      p.chequeOrBondDate,
      p.ChequeOrBondDate,
      p.deliveryDate,
      p.DeliveryDate,
      p.invoiceDate,
      p.InvoiceDate
    );
  } else {
    tryList.push(
      p.deliveryDate,
      p.DeliveryDate,
      p.invoiceDate,
      p.InvoiceDate,
      p.chequeOrBondDate,
      p.ChequeOrBondDate
    );
  }

  // Önce mantıklı (2000 ve sonrası) tarihleri dene
  for (const raw of tryList) {
    const d = toDate(raw);
    if (d && d.getFullYear() >= 2000) return d;
  }

  // Hiç mantıklı tarih yoksa ama fallback verilmişse onu kullan
  if (fallbackDate) {
    return new Date(fallbackDate);
  }

  // Yine yoksa ilk bulduğunu dön (1970 bile olsa)
  for (const raw of tryList) {
    const d = toDate(raw);
    if (d) return d;
  }
  return null;
};

const monthLabelTR = (date) =>
  date.toLocaleString("tr-TR", { month: "long", year: "numeric" });

const formatTL = (v, fraction = 0) =>
  Number(v || 0).toLocaleString("tr-TR", {
    maximumFractionDigits: fraction,
    minimumFractionDigits: fraction,
  });

// Uzatma ay + günlerini de dahil ederek toplam ay sayısı
const getTotalMonthsForRental = (r) => {
  const baseMonths = Number(r.rentalPeriod || 0);
  const extraMonths = Number(r.extraPeriodMonth || 0);
  const extraDays = Number(r.extraPeriod ?? r.extratPeriod ?? 0);
  const dayToMonth = extraDays / 30;
  const total = baseMonths + extraMonths + dayToMonth;
  return total > 0 ? total : 1;
};

// Yatırımcı hakedişi (backend'de hesaplanmış)
const getTotalInvestorPaymentForRental = (r) => Number(r.serviceFee || 0);

// Kiralama bitişlerinin en büyüğü
const getMaxRentalEndDate = (rentals) => {
  let max = null;
  rentals.forEach((r) => {
    const end = toDate(r.endDate);
    if (!end) return;
    if (!max || end > max) max = end;
  });
  return max;
};

// Eski tarihli ödemeler için fallback: ilk kiralamanın başlangıç tarihi
const getFallbackRentalStart = (rentals) => {
  const dates = rentals
    .map((r) => toDate(r.startDate))
    .filter(Boolean)
    .sort((a, b) => a - b);
  return dates[0] || null;
};

// Bugünkü yıl/ay
const now = new Date();
const NOW_YEAR = now.getFullYear();
const NOW_MONTH = now.getMonth();

// yıl + ay -> index
const ymIndex = (y, m) => y * 12 + m;

// ---------- Ana Bileşen ----------

export default function CompanyPaymentPlanPage() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("ALL"); // ALL = tüm makine sahipleri

  const [companyReports, setCompanyReports] = useState([]); // { companyId, report }
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [error, setError] = useState("");
  const [reportError, setReportError] = useState("");

  // görünüm modu: raw = ödeme tarihine göre, allocated = mahsuplu
  const [viewMode, setViewMode] = useState("raw");

  // yıl aralığı filtresi
  const [filterStartYear, setFilterStartYear] = useState(null);
  const [filterEndYear, setFilterEndYear] = useState(null);

  // -------- FİRMALAR --------
  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      setError("");
      try {
        const res = await api.get("/Company");
        const list = res?.data?.data ?? res?.data ?? [];
        setCompanies(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error(err);
        setError("Firmalar yüklenirken bir hata oluştu.");
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, []);

  // Sadece makine sahibi firmalar
  const ownerCompanies = useMemo(
    () => companies.filter((c) => c.hasTowerCrane === true),
    [companies]
  );

  const getCompanyName = (id) => {
    if (!id || id === "ALL") return "Tüm Makine Sahibi Firmalar";
    const c = ownerCompanies.find((x) => String(x.id) === String(id));
    return c?.name || "Bilinmeyen Firma";
  };

  // Firma değişince görünümü resetle
  useEffect(() => {
    setViewMode("raw");
  }, [selectedCompanyId]);

  // -------- RAPORLAR --------
  useEffect(() => {
    if (!ownerCompanies.length) {
      setCompanyReports([]);
      return;
    }

    const fetchReports = async () => {
      setLoadingReports(true);
      setReportError("");
      try {
        if (selectedCompanyId === "ALL") {
          const promises = ownerCompanies.map((c) =>
            api
              .get(`/Rental/Report/${c.id}`)
              .then((res) => ({
                companyId: c.id,
                report: res.data?.data ?? res.data ?? null,
              }))
              .catch((err) => {
                console.error("Rapor alınamadı:", c.name, err);
                return null;
              })
          );

          const results = await Promise.all(promises);
          setCompanyReports(results.filter(Boolean));
        } else {
          const res = await api.get(`/Rental/Report/${selectedCompanyId}`);
          setCompanyReports([
            {
              companyId: selectedCompanyId,
              report: res.data?.data ?? res.data ?? null,
            },
          ]);
        }
      } catch (err) {
        console.error(err);
        setReportError("Raporlar çekilirken bir hata oluştu.");
        setCompanyReports([]);
      } finally {
        setLoadingReports(false);
      }
    };

    fetchReports();
  }, [selectedCompanyId, ownerCompanies]);

  // -------- HESAPLAMALAR --------
  const calcResult = useMemo(() => {
    if (!companyReports.length) {
      return {
        months: [],
        totalPlanned: 0,
        totalPaidRaw: 0,
        totalPaidAllocated: 0,
        totalEarly: 0,
        totalLate: 0,
        companyStats: [],
        allYears: [],
        suggestion: null,
      };
    }

    // =========================
    // =   TÜM FİRMALAR MODU   =
    // =========================
    if (selectedCompanyId === "ALL") {
      const globalPlanByMonth = new Map();
      const globalPaidByMonth = new Map();
      const companyPlanByMonth = new Map();
      const companyPaidByMonth = new Map();

      let minDate = null;
      let maxDate = null;

      // Backend toplamları (Yatırımcı Muhasebesi ile aynı)
      let totalPlanned = 0;
      let totalPaidRaw = 0;

      companyReports.forEach(({ companyId, report }) => {
        if (!report) return;

        const rentals = report.rentals ?? [];
        const payments = report.payments ?? [];
        const cid = String(companyId);

        totalPlanned += Number(report.totalProgressPayment || 0);
        totalPaidRaw += Number(report.totalNonConstructionPayment || 0);

        if (!companyPlanByMonth.has(cid)) companyPlanByMonth.set(cid, new Map());
        if (!companyPaidByMonth.has(cid)) companyPaidByMonth.set(cid, new Map());
        const planMap = companyPlanByMonth.get(cid);
        const paidMap = companyPaidByMonth.get(cid);

        const rentalsForPlan = rentals;
        const fallbackStart = getFallbackRentalStart(rentalsForPlan);

        // --- Planlanan ödemeler (yatırımcı hakedişi) ---
        rentalsForPlan.forEach((r) => {
          const start = toDate(r.startDate);
          const end = toDate(r.endDate);
          if (!start || !end) return;

          const totalMonths = getTotalMonthsForRental(r);
          const totalInvestor = getTotalInvestorPaymentForRental(r);
          const monthlyInvestor = totalInvestor / totalMonths;

          let payDate = new Date(start.getTime());
          payDate.setDate(payDate.getDate() + 10); // ilk ödeme: başlangıç + 10 gün

          while (payDate <= end) {
            const year = payDate.getFullYear();
            const month = payDate.getMonth();
            const key = `${year}-${month}`;

            const prevG = globalPlanByMonth.get(key) || 0;
            globalPlanByMonth.set(key, prevG + monthlyInvestor);

            const prevC = planMap.get(key) || 0;
            planMap.set(key, prevC + monthlyInvestor);

            if (!minDate || payDate < minDate) minDate = new Date(payDate);
            if (!maxDate || payDate > maxDate) maxDate = new Date(payDate);

            const next = new Date(payDate.getTime());
            next.setMonth(next.getMonth() + 1);
            payDate = next;
          }
        });

        // --- Ödemeler (constructionPayment !== true) ---
        const nonConstructionPayments = payments.filter(
          (p) => p.constructionPayment !== true
        );

        nonConstructionPayments.forEach((p) => {
          const typeRaw = (p.paymentType || "").toString().toLowerCase();
          let effDate = null;

          if (typeRaw.includes("transfer") || typeRaw.includes("havale")) {
            effDate = normalizePaymentDate(p, "transfer", fallbackStart);
          } else if (
            typeRaw.includes("cheque") ||
            typeRaw.includes("çek") ||
            typeRaw.includes("cek") ||
            typeRaw.includes("bond") ||
            typeRaw.includes("senet")
          ) {
            effDate = normalizePaymentDate(p, "cheque", fallbackStart);
          } else {
            effDate = normalizePaymentDate(p, "generic", fallbackStart);
          }

          if (!effDate) return;

          const year = effDate.getFullYear();
          const month = effDate.getMonth();
          const key = `${year}-${month}`;
          const amount = Number(p.amount || 0);

          const prevG = globalPaidByMonth.get(key) || 0;
          globalPaidByMonth.set(key, prevG + amount);

          const prevC = paidMap.get(key) || 0;
          paidMap.set(key, prevC + amount);

          if (!minDate || effDate < minDate) minDate = new Date(effDate);
          if (!maxDate || effDate > maxDate) maxDate = new Date(effDate);
        });
      });

      if (!minDate || !maxDate) {
        return {
          months: [],
          totalPlanned,
          totalPaidRaw,
          totalPaidAllocated: totalPaidRaw,
          totalEarly: 0,
          totalLate: 0,
          companyStats: [],
          allYears: [],
          suggestion: null,
        };
      }

      const startDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
      const endDate = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

      const months = [];
      const yearSet = new Set();
      let cursor = new Date(startDate.getTime());
      while (cursor <= endDate) {
        const year = cursor.getFullYear();
        const month = cursor.getMonth();
        const key = `${year}-${month}`;

        const planned = globalPlanByMonth.get(key) || 0;
        const rawPaid = globalPaidByMonth.get(key) || 0;

        months.push({
          key,
          label: monthLabelTR(cursor),
          year,
          month,
          planned,
          rawPaid,
          allocatedPaid: rawPaid, // ALL modunda basit görünüm
          remainingAllocated: Math.max(0, planned - rawPaid),
          noteUsesThisMonthForOldDebts: false,
          chequeIssuedThisMonth: 0,
          settledAtIndex: null,
        });
        yearSet.add(year);

        cursor.setMonth(cursor.getMonth() + 1);
      }

      // erken/geç ödeme (sadece geçmiş aylar, raw'a göre)
      let totalEarly = 0;
      let totalLate = 0;
      months.forEach((m) => {
        if (ymIndex(m.year, m.month) >= ymIndex(NOW_YEAR, NOW_MONTH)) return;
        const early = Math.max(0, m.rawPaid - m.planned);
        const late = Math.max(0, m.planned - m.rawPaid);
        totalEarly += early;
        totalLate += late;
      });

      // Firma bazlı özetler (ay bazlı dağılıma göre)
      const companyStats = [];
      ownerCompanies.forEach((c) => {
        const cid = String(c.id);
        const planMap = companyPlanByMonth.get(cid) || new Map();
        const paidMap = companyPaidByMonth.get(cid) || new Map();
        const allKeys = new Set([...planMap.keys(), ...paidMap.keys()]);
        if (allKeys.size === 0) return;

        let cPlanned = 0;
        let cPaid = 0;
        let cEarly = 0;
        let cLate = 0;

        allKeys.forEach((key) => {
          const p = planMap.get(key) || 0;
          const a = paidMap.get(key) || 0;
          cPlanned += p;
          cPaid += a;
          cEarly += Math.max(0, a - p);
          cLate += Math.max(0, p - a);
        });

        companyStats.push({
          companyId: c.id,
          name: c.name,
          planned: cPlanned,
          paid: cPaid,
          early: cEarly,
          late: cLate,
        });
      });

      const allYears = Array.from(yearSet).sort((a, b) => a - b);

      // Mahsuplu toplam, ALL modunda basitçe planlanan - fiili ödeme
      const totalPaidAllocated = totalPaidRaw;

      return {
        months,
        totalPlanned,
        totalPaidRaw,
        totalPaidAllocated,
        totalEarly,
        totalLate,
        companyStats,
        allYears,
        suggestion: null,
      };
    }

    // =============================
    // =   TEK FİRMA (DETAY) MODU  =
    // =============================
    const { report } = companyReports[0];
    if (!report) {
      return {
        months: [],
        totalPlanned: 0,
        totalPaidRaw: 0,
        totalPaidAllocated: 0,
        totalEarly: 0,
        totalLate: 0,
        companyStats: [],
        allYears: [],
        suggestion: null,
      };
    }

    const rentalsAll = report.rentals ?? [];
    const rentals = rentalsAll;
    const payments = report.payments ?? [];

    // Backend toplamları (AccountPage ile bire bir aynı)
    let totalPlanned = Number(report.totalProgressPayment || 0);
    let totalPaidRaw = Number(report.totalNonConstructionPayment || 0);

    const planByMonth = new Map();
    const paidByMonth = new Map();
    const chequeIssuedByMonth = new Map();

    let minDate = null;
    let maxDate = null;

    const fallbackStart = getFallbackRentalStart(rentals);

    // --- Planlanan ---
    rentals.forEach((r) => {
      const start = toDate(r.startDate);
      const end = toDate(r.endDate);
      if (!start || !end) return;

      const totalMonths = getTotalMonthsForRental(r);
      const totalInvestor = getTotalInvestorPaymentForRental(r);
      const monthlyInvestor = totalInvestor / totalMonths;

      let payDate = new Date(start.getTime());
      payDate.setDate(payDate.getDate() + 10);

      while (payDate <= end) {
        const year = payDate.getFullYear();
        const month = payDate.getMonth();
        const key = `${year}-${month}`;
        const prev = planByMonth.get(key) || 0;
        planByMonth.set(key, prev + monthlyInvestor);

        if (!minDate || payDate < minDate) minDate = new Date(payDate);
        if (!maxDate || payDate > maxDate) maxDate = new Date(payDate);

        const next = new Date(payDate.getTime());
        next.setMonth(next.getMonth() + 1);
        payDate = next;
      }
    });

    // --- Ödemeler ---
    const nonConstructionPayments = payments.filter(
      (p) => p.constructionPayment !== true
    );

    nonConstructionPayments.forEach((p) => {
      const typeRaw = (p.paymentType || "").toString().toLowerCase();
      let effDate = null;
      let chequeIssueDate = null;

      if (typeRaw.includes("transfer") || typeRaw.includes("havale")) {
        effDate = normalizePaymentDate(p, "transfer", fallbackStart);
      } else if (
        typeRaw.includes("cheque") ||
        typeRaw.includes("çek") ||
        typeRaw.includes("cek") ||
        typeRaw.includes("bond") ||
        typeRaw.includes("senet")
      ) {
        effDate = normalizePaymentDate(p, "cheque", fallbackStart);
        chequeIssueDate = normalizePaymentDate(p, "transfer", fallbackStart);
      } else {
        effDate = normalizePaymentDate(p, "generic", fallbackStart);
      }

      const amount = Number(p.amount || 0);
      if (effDate) {
        const year = effDate.getFullYear();
        const month = effDate.getMonth();
        const key = `${year}-${month}`;

        const prev = paidByMonth.get(key) || 0;
        paidByMonth.set(key, prev + amount);

        if (!minDate || effDate < minDate) minDate = new Date(effDate);
        if (!maxDate || effDate > maxDate) maxDate = new Date(effDate);
      }

      // çek/senet düzenlenme ayı notu
      if (chequeIssueDate) {
        const year = chequeIssueDate.getFullYear();
        const month = chequeIssueDate.getMonth();
        const key = `${year}-${month}`;
        const prev = chequeIssuedByMonth.get(key) || 0;
        chequeIssuedByMonth.set(key, prev + amount);

        if (!minDate || chequeIssueDate < minDate)
          minDate = new Date(chequeIssueDate);
        if (!maxDate || chequeIssueDate > maxDate)
          maxDate = new Date(chequeIssueDate);
      }
    });

    if (!minDate || !maxDate) {
      return {
        months: [],
        totalPlanned,
        totalPaidRaw,
        totalPaidAllocated: 0,
        totalEarly: 0,
        totalLate: 0,
        companyStats: [],
        allYears: [],
        suggestion: null,
      };
    }

    const startDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const endDate = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

    const months = [];
    const yearSet = new Set();
    let cursor = new Date(startDate.getTime());
    while (cursor <= endDate) {
      const year = cursor.getFullYear();
      const month = cursor.getMonth();
      const key = `${year}-${month}`;
      const planned = planByMonth.get(key) || 0;
      const rawPaid = paidByMonth.get(key) || 0;
      const chequeIssued = chequeIssuedByMonth.get(key) || 0;

      months.push({
        key,
        label: monthLabelTR(cursor),
        year,
        month,
        planned,
        rawPaid,
        allocatedPaid: 0,
        remainingAllocated: planned,
        noteUsesThisMonthForOldDebts: false,
        chequeIssuedThisMonth: chequeIssued,
        settledAtIndex: null,
      });
      yearSet.add(year);
      cursor.setMonth(cursor.getMonth() + 1);
    }

    // ---- Mahsuplu algoritma: ödemeler en eski borca gider ----
    const queue = []; // {index, remaining}
    let carryAdvance = 0; // henüz borç yokken yapılan fazla ödeme

    for (let i = 0; i < months.length; i++) {
      const m = months[i];

      // Önce bu ayın planını kuyruğa ekle
      if (m.planned > 0) {
        queue.push({ index: i, remaining: m.planned });
      }

      let available = m.rawPaid + carryAdvance;
      if (available <= 0) {
        carryAdvance = 0;
        continue;
      }

      let usedFromThisMonthForPastDebt = false;

      while (available > 0 && queue.length > 0) {
        const q = queue[0];
        const need = q.remaining;
        const alloc = Math.min(available, need);

        months[q.index].allocatedPaid += alloc;
        months[q.index].remainingAllocated -= alloc;

        if (q.index < i && alloc > 0) {
          usedFromThisMonthForPastDebt = true;
        }

        q.remaining -= alloc;
        available -= alloc;

        if (q.remaining <= 0.0001) {
          // Bu ayın borcu tamamen kapandı, kapanma ayını işaretleyelim
          if (months[q.index].settledAtIndex == null) {
            months[q.index].settledAtIndex = i;
          }
          queue.shift();
        }
      }

      carryAdvance = available; // hala fazla para varsa geleceğe avans

      if (usedFromThisMonthForPastDebt) {
        m.noteUsesThisMonthForOldDebts = true;
      }
    }

    const totalPaidAllocated = months.reduce(
      (s, m) => s + m.allocatedPaid,
      0
    );

    // ----- Erken / geç ödeme istatistiği (sadece geçmiş aylar) -----
    let totalEarly = 0;
    let totalLate = 0;
    months.forEach((m) => {
      if (ymIndex(m.year, m.month) >= ymIndex(NOW_YEAR, NOW_MONTH)) return;

      const lateAmt = Math.max(0, m.planned - m.allocatedPaid);
      totalLate += lateAmt;

      // Erken / fazla: bu ayın fiili ödemesinin kendi planını aşması
      const earlyAmt = Math.max(0, m.rawPaid - m.planned);
      totalEarly += earlyAmt;
    });

    // ----- Önerilen ödeme planı -----
    const remainingTotal =
      totalPlanned > totalPaidAllocated
        ? totalPlanned - totalPaidAllocated
        : 0;

    let suggestion = null;
    if (remainingTotal > 0) {
      // Maksimum kira bitişi + 2 ay kuralı
      const maxRentalEnd = getMaxRentalEndDate(rentals) || endDate;
      const lastAllowedPaymentDate = new Date(
        maxRentalEnd.getFullYear(),
        maxRentalEnd.getMonth() + 2,
        1
      );

      // Bugünden itibaren ay sayısı
      const startSuggestionDate = new Date(NOW_YEAR, NOW_MONTH, 1);
      const monthsForPlan = Math.max(
        1,
        ymIndex(
          lastAllowedPaymentDate.getFullYear(),
          lastAllowedPaymentDate.getMonth()
        ) -
          ymIndex(
            startSuggestionDate.getFullYear(),
            startSuggestionDate.getMonth()
          ) +
          1
      );

      const baseMonthly = remainingTotal / monthsForPlan;

      // Geçmiş gerçek ödeme kapasitesine bak (son 6 ay ortalaması)
      const last6 = months.filter(
        (m) =>
          ymIndex(m.year, m.month) < ymIndex(NOW_YEAR, NOW_MONTH) &&
          ymIndex(m.year, m.month) >= ymIndex(NOW_YEAR, NOW_MONTH) - 6
      );
      let avgPaid = 0;
      if (last6.length) {
        avgPaid =
          last6.reduce((s, m) => s + m.rawPaid, 0) / last6.length;
      }

      const suggestedMonthly =
        avgPaid > 0 ? Math.min(baseMonthly * 1.2, avgPaid * 1.1) : baseMonthly;

      const monthSuggestions = [];
      let remaining = remainingTotal;
      let cursorDate = new Date(
        startSuggestionDate.getFullYear(),
        startSuggestionDate.getMonth(),
        1
      );

      for (let i = 0; i < monthsForPlan; i++) {
        const year = cursorDate.getFullYear();
        const month = cursorDate.getMonth();
        const key = `${year}-${month}`;

        const thisMonthBase =
          i === monthsForPlan - 1
            ? remaining
            : Math.min(remaining, suggestedMonthly);

        // Bu ay tahsil olacak çek/senetleri de info olarak göster (tahsilatlar)
        const chequeKey = `${year}-${month}`;
        const chequeThisMonth = paidByMonth.get(chequeKey) || 0;

        monthSuggestions.push({
          key,
          label: monthLabelTR(cursorDate),
          amount: thisMonthBase,
          chequeInfo: chequeThisMonth,
        });

        remaining -= thisMonthBase;
        cursorDate.setMonth(cursorDate.getMonth() + 1);
        if (remaining <= 0) break;
      }

      suggestion = {
        remainingTotal,
        monthsForPlan: monthSuggestions.length,
        monthly: suggestedMonthly,
        monthSuggestions,
      };
    }

    const allYears = Array.from(yearSet).sort((a, b) => a - b);

    return {
      months,
      totalPlanned,
      totalPaidRaw,
      totalPaidAllocated,
      totalEarly,
      totalLate,
      companyStats: [],
      allYears,
      suggestion,
    };
  }, [companyReports, selectedCompanyId, ownerCompanies]);

  const {
    months,
    totalPlanned,
    totalPaidRaw,
    totalPaidAllocated,
    totalEarly,
    totalLate,
    companyStats,
    allYears,
    suggestion,
  } = calcResult;

  // Kartlarda gösterilecek "Toplam Yapılan Ödeme"
  // -> her zaman fiili toplam ödeme (backend ile bire bir)
  const totalPaidForCards = totalPaidRaw;

// Mahsuplu kalan (algoritmaya göre) – açıklamalarda kullanıyoruz
const totalRemaining =
  totalPlanned > totalPaidAllocated
    ? totalPlanned - totalPaidAllocated
    : 0;

// Kartta gösterilecek basit kalan: Planlanan - Fiili Ödeme
const totalRemainingSimple =
  totalPlanned > totalPaidRaw
    ? totalPlanned - totalPaidRaw
    : 0;

  // yıl filtresi default
  useEffect(() => {
    if (!allYears.length) {
      setFilterStartYear(null);
      setFilterEndYear(null);
      return;
    }
    setFilterStartYear(allYears[0]);
    setFilterEndYear(allYears[allYears.length - 1]);
  }, [allYears]);

  const filteredMonths = useMemo(() => {
    if (!months.length) return [];
    if (!filterStartYear || !filterEndYear) return months;
    return months.filter(
      (m) => m.year >= filterStartYear && m.year <= filterEndYear
    );
  }, [months, filterStartYear, filterEndYear]);

  // Firma bazlı analizler (sadece ALL için)
  const unpaidCompanies =
    selectedCompanyId === "ALL"
      ? companyStats
          .filter((s) => s.planned > 0 && s.paid === 0)
          .sort((a, b) => b.planned - a.planned)
      : [];

  const mostLateCompanies =
    selectedCompanyId === "ALL"
      ? companyStats
          .filter((s) => s.late > 0)
          .sort((a, b) => b.late - a.late)
      : [];

  const mostEarlyCompanies =
    selectedCompanyId === "ALL"
      ? companyStats
          .filter((s) => s.early > 0)
          .sort((a, b) => b.early - a.early)
      : [];

  return (
    <>
      <Navbar />
      <div className="payment-plan-page">
        <div className="payment-plan-page__content">
          {/* Başlık + Firma seçimi */}
          <div className="payment-plan-page__header">
            <div>
              <h1 className="payment-plan-page__title">Firma Ödeme Planı</h1>
              <div className="payment-plan-page__subtitle">
                {getCompanyName(selectedCompanyId)}
              </div>
            </div>

            <div className="payment-plan-page__filters">
              <div className="payment-plan-page__filter">
                <label>Firma</label>
                <select
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                >
                  <option value="ALL">Tüm Makine Sahipleri</option>
                  {ownerCompanies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Yıl aralığı filtresi */}
              {allYears.length > 0 && (
                <div className="payment-plan-page__filter payment-plan-page__filter--years">
                  <label>Yıl Aralığı</label>
                  <div className="payment-plan-page__year-range">
                    <select
                      value={filterStartYear ?? ""}
                      onChange={(e) =>
                        setFilterStartYear(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                    >
                      {allYears.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                    <span>–</span>
                    <select
                      value={filterEndYear ?? ""}
                      onChange={(e) =>
                        setFilterEndYear(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                    >
                      {allYears.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Görünüm Toggle (sadece tek firma iken) */}
              {selectedCompanyId !== "ALL" && (
                <div className="payment-plan-page__filter">
                  <label>Görünüm</label>
                  <div className="payment-plan-page__toggle">
                    <button
                      type="button"
                      className={
                        viewMode === "raw"
                          ? "toggle-btn toggle-btn--active"
                          : "toggle-btn"
                      }
                      onClick={() => setViewMode("raw")}
                    >
                      Ödeme Tarihine Göre
                    </button>
                    <button
                      type="button"
                      className={
                        viewMode === "allocated"
                          ? "toggle-btn toggle-btn--active"
                          : "toggle-btn"
                      }
                      onClick={() => setViewMode("allocated")}
                    >
                      Mahsuplu Görünüm
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {error && <div className="payment-plan-page__error">{error}</div>}
          {reportError && (
            <div className="payment-plan-page__error">{reportError}</div>
          )}
          {(loadingCompanies || loadingReports) && (
            <div className="payment-plan-page__info">Veriler yükleniyor...</div>
          )}

          {/* Özet kartlar */}
          <div className="payment-plan-page__cards">
            <div className="payment-plan-page__card">
              <div className="payment-plan-page__card-label">
                Toplam Planlanan Ödeme (Net)
              </div>
              <div className="payment-plan-page__card-value">
                {formatTL(totalPlanned, 2)} TL
              </div>
            </div>

            <div className="payment-plan-page__card payment-plan-page__card--success">
              <div className="payment-plan-page__card-label">
                Toplam Yapılan Ödeme
              </div>
              <div className="payment-plan-page__card-value">
                {formatTL(totalPaidForCards, 2)} TL
              </div>
            </div>

            <div className="payment-plan-page__card payment-plan-page__card--danger">
              <div className="payment-plan-page__card-label">
                Kalan Ödeme (Mahsuplu Toplam)
              </div>
              <div className="payment-plan-page__card-value">
                {formatTL(totalRemainingSimple, 2)} TL
              </div>
            </div>
          </div>

          {/* Önerilen ödeme planı (tek firma) */}
          {selectedCompanyId !== "ALL" && suggestion && (
            <div className="payment-plan-page__suggestion">
              <div className="payment-plan-page__suggestion-title">
                Önerilen ödeme planı
              </div>
              <div className="payment-plan-page__suggestion-intro">
                {getCompanyName(selectedCompanyId)} için kalan{" "}
                <strong>{formatTL(suggestion.remainingTotal, 2)} TL</strong>{" "}
                bakiyeyi, bugünden itibaren yaklaşık{" "}
                <strong>{suggestion.monthsForPlan} ay</strong> boyunca her ay
                ortalama{" "}
                <strong>{formatTL(suggestion.monthly, 0)} TL</strong> ödeyerek
                kapatabilirsiniz.
              </div>
              <div className="payment-plan-page__suggestion-chips">
                {suggestion.monthSuggestions.map((m) => (
                  <div
                    key={m.key}
                    className="payment-plan-page__suggestion-chip"
                  >
                    <span className="payment-plan-page__suggestion-chip-label">
                      {m.label}:
                    </span>
                    <span className="payment-plan-page__suggestion-chip-amount">
                      {formatTL(m.amount, 0)} TL
                    </span>
                    {m.chequeInfo > 0 && (
                      <span className="payment-plan-page__suggestion-chip-note">
                        ({formatTL(m.chequeInfo, 0)} TL çek/senet tahsil)
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ay bazlı kartlar */}
          {!filteredMonths.length && !loadingReports ? (
            <div className="payment-plan-page__info">
              Seçilen kriterlere göre ödeme planı bulunamadı.
            </div>
          ) : (
            <div className="payment-plan-page__calendar">
              {filteredMonths.map((m) => {
                const usedPaid =
                  viewMode === "allocated" ? m.allocatedPaid : m.rawPaid;
                const remaining =
                  viewMode === "allocated"
                    ? m.remainingAllocated
                    : Math.max(0, m.planned - m.rawPaid);

                let statusClass = "payment-plan-page__month--empty";
                if (m.planned > 0) {
                  if (usedPaid >= m.planned - 1) {
                    statusClass = "payment-plan-page__month--ok";
                  } else if (usedPaid > 0 && usedPaid < m.planned) {
                    statusClass = "payment-plan-page__month--partial";
                  } else {
                    statusClass = "payment-plan-page__month--missing";
                  }
                }

                return (
                  <div
                    key={m.key}
                    className={`payment-plan-page__month ${statusClass}`}
                  >
                    <div className="payment-plan-page__month-title">
                      {m.label}
                    </div>

                    <div className="payment-plan-page__month-row">
                      <span>Planlanan:</span>
                      <strong>{formatTL(m.planned, 0)} TL</strong>
                    </div>

                    <div className="payment-plan-page__month-row">
                      <span>
                        Ödenen (
                        {viewMode === "allocated" ? "kullanılan" : "fiili"}):
                      </span>
                      <strong>{formatTL(usedPaid, 0)} TL</strong>
                    </div>

                    {viewMode === "allocated" && (
                      <div className="payment-plan-page__month-row">
                        <span>Bu ay fiili ödeme:</span>
                        <strong>{formatTL(m.rawPaid, 0)} TL</strong>
                      </div>
                    )}

                    <div className="payment-plan-page__month-row">
                      <span>Kalan:</span>
                      <strong>{formatTL(Math.max(0, remaining), 0)} TL</strong>
                    </div>

                    {viewMode === "allocated" &&
                      m.noteUsesThisMonthForOldDebts && (
                        <div className="payment-plan-page__month-note">
                          Bu ay yapılan ödemenin bir kısmı önceki aylardaki
                          eksik ödemeleri kapatmak için mahsup edilmiştir.
                        </div>
                      )}

                    {m.chequeIssuedThisMonth > 0 && (
                      <div className="payment-plan-page__month-note payment-plan-page__month-note--cheque">
                        Bu ay toplam{" "}
                        <strong>
                          {formatTL(m.chequeIssuedThisMonth, 0)} TL
                        </strong>{" "}
                        tutarında ileri vadeli çek/senet verilmiştir. Tahsilat
                        tarihleri geldiğinde mahsup dağıtımına dahil
                        edilecektir.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Alt açıklama */}
          {months.length > 0 && (
            <div className="payment-plan-page__bottom-summary">
              {selectedCompanyId === "ALL" ? (
                <>
                  Tüm makine sahibi firmalar için toplam{" "}
                  <strong>{formatTL(totalPlanned, 2)} TL</strong> yatırımcı
                  ödemesi planlanmış, bunun{" "}
                  <strong>{formatTL(totalPaidRaw, 2)} TL&apos;si</strong> fiilen
                  ödenmiştir. Mahsuplu bakışta toplam{" "}
                  <strong>{formatTL(totalRemaining, 2)} TL</strong> ödeme
                  yükümlülüğü bulunmaktadır.
                </>
              ) : viewMode === "allocated" ? (
                <>
                  {getCompanyName(selectedCompanyId)} için toplam{" "}
                  <strong>{formatTL(totalPlanned, 2)} TL</strong> yatırımcı
                  ödemesi planlanmış, fiili olarak{" "}
                  <strong>{formatTL(totalPaidRaw, 2)} TL</strong> ödeme
                  yapılmıştır. Mahsuplu dağıtıma göre kalan ödeme tutarı{" "}
                  <strong>{formatTL(totalRemaining, 2)} TL</strong>&apos;dir.
                </>
              ) : (
                <>
                  {getCompanyName(selectedCompanyId)} için toplam{" "}
                  <strong>{formatTL(totalPlanned, 2)} TL</strong> yatırımcı
                  ödemesi planlanmış, bunun{" "}
                  <strong>{formatTL(totalPaidRaw, 2)} TL&apos;si</strong> fiilen
                  ödenmiştir. Mahsuplu görünümü görmek için{" "}
                  <strong>“Mahsuplu Görünüm”</strong> seçeneğini
                  kullanabilirsiniz.
                </>
              )}
              <br />
              {totalEarly > 0 || totalLate > 0 ? (
                <>
                  Bugüne kadarki dönemde (gelecek aylar hariç) toplam{" "}
                  <strong>{formatTL(totalEarly, 0)} TL</strong> erken / fazla
                  ödeme,{" "}
                  <strong>{formatTL(totalLate, 0)} TL</strong> ise geç ödeme
                  olarak gerçekleşmiştir. Geç ödemeler, ilgili ayın planlanan
                  tutarının kendi döneminde kapanmamış ve sonraki aylardaki
                  ödemelerle kapatılmış olması anlamına gelir.
                </>
              ) : (
                <>Bu dönem için anlamlı bir erken / geç ödeme farkı görünmüyor.</>
              )}

              {selectedCompanyId === "ALL" && companyStats.length > 0 && (
                <div className="payment-plan-page__firm-summary">
                  {unpaidCompanies.length > 0 && (
                    <p>
                      <strong>Hiç ödeme almamış firmalar</strong> (planlanan
                      ödeme olup fiili ödeme olmayanlar):{" "}
                      {unpaidCompanies.slice(0, 5).map((s, idx) => (
                        <span key={s.companyId}>
                          {idx > 0 && ", "}
                          {s.name} ({formatTL(s.planned, 0)} TL planlanan)
                        </span>
                      ))}
                      {unpaidCompanies.length > 5 && " ..."}
                    </p>
                  )}

                  {mostLateCompanies.length > 0 && (
                    <p>
                      <strong>En fazla geç ödeme alan firmalar</strong> (planın
                      altında kalan tutarların toplamı):{" "}
                      {mostLateCompanies.slice(0, 5).map((s, idx) => (
                        <span key={s.companyId}>
                          {idx > 0 && ", "}
                          {s.name} ({formatTL(s.late, 0)} TL geç ödeme)
                        </span>
                      ))}
                      {mostLateCompanies.length > 5 && " ..."}
                    </p>
                  )}

                  {mostEarlyCompanies.length > 0 && (
                    <p>
                      <strong>En fazla erken / fazla ödeme alan firmalar</strong>{" "}
                      (planın üzerinde gerçekleşmiş ödemeler):{" "}
                      {mostEarlyCompanies.slice(0, 5).map((s, idx) => (
                        <span key={s.companyId}>
                          {idx > 0 && ", "}
                          {s.name} (
                          {formatTL(s.early, 0)} TL fazla/erken ödeme)
                        </span>
                      ))}
                      {mostEarlyCompanies.length > 5 && " ..."}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
